import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { FeedbackQuery, GuestsApiService } from '../services/guests-api.service';

interface FeedbackFilter {
  name: string;
  query: FeedbackQuery;
}

interface RatingEntry {
  rating: number | string;
  percentage: number;
  total: number;
}

/**
 * Native-Angular port of the AngularJS people FeedbackController
 * (account-v1/.../people/feedback/feedback.controller.js) + feedback.html.
 * Replaces the `people.feedback` UIRouter state's AngularJS component
 * (Milestone 4 of the People rewrite, under the native shell from M3).
 *
 * The Past-30-Days default date range is computed locally instead of via the
 * AngularJS `snapshot` service: in a fresh QA context sessionStorage is empty,
 * so the baseline also falls back to the default — same dates either way,
 * one fewer hybrid bridge. Date serialization stays day-granular (yyyy-M-d)
 * in GuestsApiService so the recorded /api fixture key matches across envs.
 *
 * `ft-sheet-tabs` (empty tabs registry passed by the account-v2 wrapper) and
 * `ft-loading-animation` / `ft-infinite-scroll` are dropped the same way the
 * Directory rewrite dropped them — replaced by *ngIf-gated spinners and an
 * IntersectionObserver respectively. Scrolling/infinite-load mirrors v1.
 */
@Component({
    selector: 'ft-people-feedback-ng',
    templateUrl: './feedback.component.html',
    standalone: false
})
export class FeedbackComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLElement>;

  readonly filters: FeedbackFilter[] = [];
  filter!: FeedbackFilter;
  feedbacks: any[] = [];
  ratings: RatingEntry[] = [];
  hasMoreData = false;
  isProcessingRequest = false;

  private queryToken = 0;
  private observer?: IntersectionObserver;

  constructor(
    private readonly guests: GuestsApiService,
    private readonly spaces: SpacesService,
    private readonly stateService: StateService,
  ) {
    // Default range: Past 30 Days — same window predefinedDateRanges + snapshot
    // produce on a fresh app boot. moment().subtract(30,'days') / subtract(1,'days')
    // in account-v1 are wall-clock dates at boot moment; here equivalent.
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);

    const baseFetch = 24;
    this.filters = [
      {
        name: 'Most recent',
        query: { SortBy: 'create_date', SortDirection: 'desc', Offset: 0, Fetch: baseFetch, startDate, endDate },
      },
      {
        name: 'Lowest rating',
        query: { SortBy: 'internal_rating_type_id', SortDirection: 'asc', Offset: 0, Fetch: baseFetch, startDate, endDate },
      },
      {
        name: 'Highest rating',
        query: { SortBy: 'internal_rating_type_id', SortDirection: 'desc', Offset: 0, Fetch: baseFetch, startDate, endDate },
      },
    ];
    this.filter = this.clone(this.filters[0]);
  }

  ngOnInit(): void {
    this.changeFilter(this.filters[0]);
  }

  ngAfterViewInit(): void {
    if (!this.scrollSentinel) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && this.hasMoreData && !this.isProcessingRequest) {
        this.loadNextPage();
      }
    });
    this.observer.observe(this.scrollSentinel.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /** Total count comes back on the first feedback row (matches v1). */
  getTotal(): number {
    return this.feedbacks.length > 0 ? this.feedbacks[0].totalCount : 0;
  }

  /** Sum of the per-rating totals — denominator for the "guests gave their rating" supertitle. */
  getTotalRatings(): number {
    return this.ratings.reduce((memo, r) => memo + (r.total ?? 0), 0);
  }

  changeFilter(newFilter: FeedbackFilter): void {
    this.filter = this.clone(newFilter);
    this.feedbacks = [];
    this.load((data) => {
      this.feedbacks = Array.isArray(data?.stories) ? data.stories : [];
      this.ratings = adjustRatings(data?.ratings);
    });
  }

  loadNextPage(): void {
    this.filter.query.Offset += this.filter.query.Fetch;
    this.load((data) => {
      this.feedbacks = this.feedbacks.concat(data?.stories ?? []);
    });
  }

  /** ft-daterange-picker (changed) hook — reload all filters with the new range. */
  dateRangeChanged(range: { fromDate: Date; toDate: Date }): void {
    if (!range) return;
    for (const f of this.filters) {
      f.query.startDate = range.fromDate;
      f.query.endDate = range.toDate;
    }
    this.filter.query.startDate = range.fromDate;
    this.filter.query.endDate = range.toDate;
    this.filter.query.Offset = 0;
    this.changeFilter(this.filter);
  }

  details(feedback: any): void {
    this.stateService.go('people.feedback.details', { advocateUUID: feedback?.promoter?.guestUUID });
  }

  private load(onData: (data: any) => void): void {
    const token = ++this.queryToken;
    this.isProcessingRequest = true;
    this.guests.feedback(this.spaces.getPropertyUuid(), { ...this.filter.query }).subscribe({
      next: (data) => {
        if (token !== this.queryToken) return;
        const total = Array.isArray(data?.stories) && data.stories.length > 0 ? data.stories[0].totalCount : 0;
        this.hasMoreData = total > this.filter.query.Offset + this.filter.query.Fetch;
        onData(data);
      },
      complete: () => {
        if (token === this.queryToken) this.isProcessingRequest = false;
      },
      error: () => {
        if (token === this.queryToken) this.isProcessingRequest = false;
      },
    });
  }

  private clone(f: FeedbackFilter): FeedbackFilter {
    return { name: f.name, query: { ...f.query } };
  }
}

/** Reshape the API ratings ({rating, percentage, ratingTotal}) to the RatingsMeter input. */
function adjustRatings(raw: any[] | undefined): RatingEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r: any) => ({ rating: r.rating, percentage: r.percentage, total: r.ratingTotal }));
}
